import {
    observable,
    action,
    computed,
    makeObservable,
    runInAction,
} from 'mobx';

import {
    CashAcceptorStatusCode,
    PrinterStatusCode,
    CashAcceptorEvent,
} from '@b2bsoft/kiosk-shell-sdk';
import { ApplicationCloseReason } from 'sdk/index';
import {
    history,
    RoutePaths,
    LogEvents,
    logger,
    CassetteEvents,
} from 'helpers';
import i18n from 'helpers/i18n';
import StoreModel from 'models/Index';
import { WizardStep } from 'models/kiosk';

export class PeripheryStatusModel {
    @observable
    public cashAccepterStatus: CashAcceptorStatusCode;

    @observable
    public printerStatus: PrinterStatusCode;

    @observable
    public kioskOnline: boolean;

    @observable
    public isCashProcessingStarted: boolean;

    @observable
    public isCassetteInserted: boolean = true;

    public networkInterruptionTimeout: any;
    public root: StoreModel;

    cashAcceptorCodeMap: { [key: number]: string } = {
        [CashAcceptorStatusCode.Offline]: 'Cash acceptor is disconnected',
        [CashAcceptorStatusCode.CashOverflow]: 'Cash acceptor is full',
        [CashAcceptorStatusCode.NoCassette]:
            "Cash acceptor's cassette is removed",
        [CashAcceptorStatusCode.NeedUserIntervention]:
            'Cash acceptor is in failure mode Needs user intervention',

        [CashAcceptorStatusCode.Failed]:
            'Cash acceptor is failed to initialize. This is usually requires device restart',
    };

    printerCodeMap: { [key: number]: string } = {
        [PrinterStatusCode.Offline]: 'Printer is disconnected',
        [PrinterStatusCode.NoPaper]: 'Printer is out of paper',
        [PrinterStatusCode.OtherError]:
            'Printer is failed to initialize. This is usually requires device restart',
    };

    constructor(
        root: StoreModel,
        cashAccepterStatus: CashAcceptorStatusCode = CashAcceptorStatusCode.OnlineAndReady,
        printerStatus: PrinterStatusCode = PrinterStatusCode.OnlineAndReady
    ) {
        this.root = root;
        this.setCashAcceptorStatus(cashAccepterStatus);
        this.setPrinterStatus(printerStatus);
        this.kioskOnline = true;

        document.addEventListener(
            CassetteEvents.CassetteRemovedEventRaised,
            this.handleCassetteRemovedEvent.bind(this)
        );

        document.addEventListener(
            CassetteEvents.CassetteInsertedEventRaised,
            this.handleCassetteInsertedEvent.bind(this)
        );

        makeObservable(this);
    }

    @action
    private handleCassetteRemovedEvent() {
        console.log('Raised ', CassetteEvents.CassetteRemovedEventRaised);

        runInAction(() => {
            this.isCassetteInserted = false;
        });
    }

    @action
    private handleCassetteInsertedEvent() {
        console.log('Raised ', CassetteEvents.CassetteInsertedEventRaised);

        runInAction(() => {
            this.isCassetteInserted = true;
        });
    }

    @action
    setValue(value: any) {
        Object.assign(this, value);
    }

    @action
    setPrinterStatus = (printerStatus: PrinterStatusCode) => {
        this.printerStatus = printerStatus;
    };

    @action
    setCashAcceptorStatus = (cashAccepterStatus: CashAcceptorStatusCode) => {
        this.cashAccepterStatus = cashAccepterStatus;
        this.isCassetteInserted =
            cashAccepterStatus !== CashAcceptorStatusCode.NoCassette;
    };

    @action
    check = async () => {
        const { shellClient } = this.root;

        this.cashAccepterStatus = await shellClient.cashAcceptor.getStatus();
        this.printerStatus = await shellClient.getPrinterStatus();

        return !this.hasErrors;
    };

    @action
    handleConnectionChange = async (event: 'offline' | 'online') => {
        const { loader, configuration } = this.root;

        if (event === 'offline') {
            this.kioskOnline = false;

            loader.show(
                i18n.t(
                    'Internet connection was lost please wait while we are restoring it'
                )
            );

            logger.logInformation(
                'Internet connection was lost',
                LogEvents.InternetConnectionLost,
                {}
            );
            logger.localLog(
                'Internet connection was lost',
                LogEvents.InternetConnectionLost
            );

            this.networkInterruptionTimeout = setTimeout(() => {
                this.root.sdk
                    .closeApp(ApplicationCloseReason.NoInternetConnection)
                    .then(() => {
                        if (
                            this.root.wizard.currentStep !==
                                WizardStep.PrintReceipt &&
                            this.root.wizard.currentStep !== WizardStep.Farewell
                        ) {
                            history.push({
                                pathname: RoutePaths.KioskOutOfOrder,
                                search: window.location.search,
                            });
                        }
                    })
                    .catch((error: any) => {
                        logger.logInformation(
                            'Application was not closed',
                            LogEvents.AppNotClosed,
                            error
                        );
                    })
                    .finally(() => {
                        loader.hide();
                    });
            }, configuration.app.networkInterruptionWaitTime * 1000);
        }

        if (event === 'online') {
            loader.hide();

            await logger.logInformation(
                'Internet connection is restored',
                LogEvents.InternetConnectionRestored,
                {}
            );

            await logger.sendStackedLogMessages();

            this.kioskOnline = true;

            this.networkInterruptionTimeout &&
                clearTimeout(this.networkInterruptionTimeout);
        }
    };

    @action
    handleCashAcceptorEvent = (event: CashAcceptorEvent) => {
        if (event === CashAcceptorEvent.Started) {
            this.setCashProcessingStarted(true);
        }

        if (
            event === CashAcceptorEvent.Stopped ||
            event === CashAcceptorEvent.BanknoteInserted ||
            event === CashAcceptorEvent.BanknoteReturned
        ) {
            this.setCashProcessingStarted(false);
        }
    };

    @action
    setCashProcessingStarted = (value: boolean) => {
        this.isCashProcessingStarted = value;
    };

    @computed
    get isCashAccepterReady() {
        return (
            this.cashAccepterStatus === CashAcceptorStatusCode.OnlineAndReady
        );
    }

    @computed
    get cashAccepterError() {
        return (
            this.cashAcceptorCodeMap[this.cashAccepterStatus] ||
            this.cashAcceptorCodeMap[
                CashAcceptorStatusCode.NeedUserIntervention
            ]
        );
    }

    @computed
    get isPrinterReady() {
        return this.printerStatus === PrinterStatusCode.OnlineAndReady;
    }

    @computed
    get isPrinterOutOfPaper() {
        return this.printerStatus === PrinterStatusCode.NoPaper;
    }

    @computed
    get isKioskOnline() {
        return this.kioskOnline;
    }

    @computed
    get printerError() {
        return (
            this.printerCodeMap[this.printerStatus] ||
            this.printerCodeMap[PrinterStatusCode.OtherError]
        );
    }

    @computed
    get hasErrors() {
        return (
            !this.isCashAccepterReady ||
            !this.isPrinterReady ||
            !this.isKioskOnline
        );
    }
}

export default PeripheryStatusModel;
